import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { UserRolesMap } from "../../../../common/constants";
import { useAppContext } from "../../../../common/context/AppContext";
import { useBuyerSteps } from "../../../../common/hooks";
import { BuyerService } from "../../../../common/services/BuyerService";
import { BuyerRoles } from "../../../../common/types";
import { HookedForm } from "../../../components/HookedForm";
import { HookedRadioGroup } from "../../../components/HookedRadioGroup";
import { BuyersStep, RoutePaths } from "../../../consts/enums";
import { EventName, trackEvent } from "../../../../common/utils/helpers";

const acquisitionsOptions = [
  { label: "None", value: "0" },
  { label: "Just the one", value: "1" },
  { label: "2 to 5", value: "2" },
  { label: "5 to 9", value: "3" },
  { label: "10+", value: "4" },
];

const options = [
  {
    label: UserRolesMap.get(BuyerRoles.AcquisitionEntrepreneur),
    value: BuyerRoles.AcquisitionEntrepreneur as string,
  },
  {
    label: UserRolesMap.get(BuyerRoles.Angel),
    value: BuyerRoles.Angel as string,
  },
  {
    label: UserRolesMap.get(BuyerRoles.CorporateDevelopment),
    value: BuyerRoles.CorporateDevelopment as string,
  },
  {
    label: UserRolesMap.get(BuyerRoles.FundManager),
    value: BuyerRoles.FundManager as string,
  },
  {
    label: UserRolesMap.get(BuyerRoles.PrivateEquity),
    value: BuyerRoles.PrivateEquity as string,
  },
  {
    label: UserRolesMap.get(BuyerRoles.VentureCapital),
    value: BuyerRoles.VentureCapital as string,
  },
  {
    label: UserRolesMap.get(BuyerRoles.Other),
    value: BuyerRoles.Other as string,
  },
];

const schema = yup
  .object({
    role: yup.string().required().nullable().trim().label("Type of buyer"),
    acquisitions_projected: yup
      .string()
      .nullable()
      .required()
      .min(1)
      .label("Planned acquisitions"),
    acquisitions_previously: yup
      .string()
      .nullable()
      .required()
      .min(1)
      .label("Acquisitions made"),
  })
  .required();

export const BuyerType = () => {
  const { user, updateUser } = useAppContext();
  const { data, isLoading } = useBuyerSteps(BuyersStep.TYPE);
  const navigate = useNavigate();
  const formHook = useForm({
    defaultValues: data,
    resolver: yupResolver(schema),
  });

  const handleNext = (values: any) => {
    BuyerService.PatchBuyer({ ...values });

    updateUser({ ...user, buyerFull: { ...user.buyerFull!, ...values } });
    navigate(RoutePaths.REGISTER_ACQUIRER + "/categories");
  };

  useEffect(() => {
    if (data && !isLoading) {
      formHook.reset({ ...data });
    }
  }, [data, isLoading]);

  return (
    <Box
      sx={{
        backgroundColor: "common.black",
        backgroundImage: "url(/images/BiggerGradient.png)",
        backgroundSize: "cover",
        backgroundPosition: "right",
        backgroundRepeat: "no-repeat",
        width: "100%",
        minHeight: "100%",
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          padding: 4,
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ color: "white" }}>
            PUBLIC INFORMATION
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "common.grayOpacity" }}>
            Other Foundy users can view these details
          </Typography>
          <Paper
            variant="outlined"
            elevation={2}
            sx={{ marginTop: 4, padding: 3 }}
          >
            <Typography variant="h3" sx={{ marginBottom: 1 }}>
              What type of buyer are you?
            </Typography>

            <HookedForm formHook={formHook}>
              <HookedRadioGroup
                name="role"
                options={options}
                defaultValue={BuyerRoles.AcquisitionEntrepreneur as string}
              />

              <Box>
                <Box
                  component="hr"
                  sx={{
                    marginY: 3,
                    width: "100%",
                    borderColor: "common.lightgray",
                    borderStyle: "solid",
                  }}
                />
                <Typography variant="h3" sx={{ marginY: 1 }}>
                  In the last 5 years, how many business acquisitions have you
                  made?
                </Typography>

                <HookedRadioGroup
                  name="acquisitions_previously"
                  options={acquisitionsOptions}
                />
                <Box
                  component="hr"
                  sx={{
                    marginY: 3,
                    width: "100%",
                    borderColor: "common.lightgray",
                    borderStyle: "solid",
                  }}
                />
                <Typography variant="h3" sx={{ marginY: 1 }}>
                  In the next year, how many business acquisitions do you plan
                  to make?
                </Typography>

                <HookedRadioGroup
                  name="acquisitions_projected"
                  options={acquisitionsOptions}
                />
              </Box>
            </HookedForm>
          </Paper>
          <Box
            sx={{
              textAlign: "right",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={formHook.handleSubmit(handleNext)}
              sx={{ marginTop: 2 }}
              variant="roundgradient"
            >
              Save &amp; Continue
            </Button>
          </Box>
        </Box>
        <Box sx={{ marginTop: 8 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="white"
            sx={{ marginBottom: 1 }}
          >
            Just want to enter the marketplace?
          </Typography>
          <Typography
            variant="body2"
            color="common.gray"
            sx={{ marginBottom: 2 }}
          >
            Come back later to complete your Professional Buyer Profile when you
            want to start contacting sellers.
          </Typography>
          <Button
            variant="roundprimary"
            onClick={() => {
              trackEvent(EventName.SKIP_SIGN_UP);
              navigate(RoutePaths.YOU_ACQUISITION);
            }}
          >
            <span>Skip</span>
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
